import React from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Button } from '../../../components/ui/Button';
import { createSchool } from '../../../modules/platform/api/devteam';
import { showErrorToast, showSuccessToast } from '../../../utils/errorHandler';
import { useEffect, useState } from 'react';

interface Props {
  onSuccess?: () => void;
  onCancel?: () => void;
  showFooter?: boolean;
}

interface FormData {
  name: string;
  subdomain: string;
  primary_domain?: string;
  timezone: string;
  country: string;
  email: string;
  phone?: string;
  address?: string;
  logo_url?: string;
  theme_color?: string;
}

export const CreateSchoolForm: React.FC<Props> = ({ onSuccess, onCancel, showFooter = true }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      theme_color: '#4f46e5',
    },
  });

  // local state for preview reactivity (avoids extra renders from watch)
  const [logoValue, setLogoValue] = useState<string | undefined>(undefined);

  useEffect(() => {
    const sub = watch((value) => {
      setLogoValue(value.logo_url);
    });
    return () => sub.unsubscribe && sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const onSubmit = async (values: FormData) => {
    const payload = {
      name: values.name,
      subdomain: values.subdomain,
      primary_domain: values.primary_domain || null,
      timezone: values.timezone,
      country: values.country,
      contact: {
        email: values.email,
        phone: values.phone || null,
        address: values.address || null,
      },
      branding: {
        logo_url: values.logo_url || null,
        theme_color: values.theme_color || '#4f46e5',
      },
    };

    try {
      // createSchool expects CreateSchoolRequest but backend accepts extended payload.
      await createSchool(payload as any);
      showSuccessToast('School created successfully!');
      onSuccess && onSuccess();
    } catch (err) {
      showErrorToast(err);
    }
  };

  return (
    <form id="create-school-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label="School Name" {...register('name', { required: 'Required' })} error={errors.name?.message} />
        <Input label="Primary Domain" {...register('primary_domain')} />
        <Input label="Subdomain" {...register('subdomain', { required: 'Required' })} error={errors.subdomain?.message} />
        <Input label="Timezone" {...register('timezone', { required: 'Required' })} error={errors.timezone?.message} />
        <Input label="Country" {...register('country', { required: 'Required' })} error={errors.country?.message} />
        <Input label="Contact Email" {...register('email', { required: 'Required' })} error={errors.email?.message} />
      </div>

      <Textarea label="Address" {...register('address')} rows={3} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
        {/* Logo URL + preview */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input label="Logo URL" {...register('logo_url')} />
          </div>
          <div className="w-24 h-24 rounded border bg-gray-50 dark:bg-gray-800 flex items-center justify-center overflow-hidden">
            {/* Preview */}
            <LogoPreview url={watch('logo_url')} />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Theme Color</label>
        <div className="mt-2 flex items-center gap-3">
          <input
            type="color"
            {...register('theme_color')}
            className="h-10 w-12 rounded border"
            aria-label="Theme color"
            onChange={(e) => {
              // ensure react-hook-form value updates
              setValue('theme_color', e.target.value);
            }}
          />
          <input
            type="text"
            value={watch('theme_color') || ''}
            onChange={(e) => setValue('theme_color', e.target.value)}
            className="h-10 px-3 rounded border w-32 text-sm"
          />
        </div>
        {errors.theme_color && <p className="text-xs text-red-500 mt-1">{String(errors.theme_color?.message)}</p>}
      </div>

      { /* Footer is rendered by parent modal when showFooter is false */ }
      { showFooter && (
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} variant="primary">
            Create
          </Button>
        </div>
      ) }
    </form>
  );
};

export default CreateSchoolForm;

// Small preview component placed below/next to the Logo URL input
const LogoPreview: React.FC<{ url?: string }> = ({ url }) => {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setError(false);
    setLoaded(false);
  }, [url]);

  if (!url) {
    return <div className="text-xs text-gray-400 px-2">No preview</div>;
  }

  return (
    <>
      {!error ? (
        // eslint-disable-next-line jsx-a11y/img-redundant-alt
        <img
          src={url}
          alt="Logo preview"
          className={`object-contain w-full h-full ${loaded ? '' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      ) : (
        <div className="text-xs text-gray-400 px-2 text-center">Invalid image</div>
      )}
    </>
  );
};
